// Fechas de la Agenda (OTT-2011). Los días se agrupan en días naturales de Europe/Madrid
// (especificación, supuestos 17); la hora de la tarjeta, en cambio, se pinta en la zona del
// navegador. `start`/`finish` llegan en epoch SEGUNDOS.

export const AGENDA_TIMEZONE = "Europe/Madrid";
export const AGENDA_HORIZON_DAYS = 14;
// Directo sin `finish` (null o 0): se da por en directo hasta start + 2 h (contrato §16/§18).
export const AGENDA_DEFAULT_LIVE_SECONDS = 2 * 60 * 60;
// upcoming=true excluye lo que dura más de 24 h (canales 24/7, FAST, radios; contrato §18).
export const AGENDA_MAX_DURATION_SECONDS = 24 * 60 * 60;

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

// "en-CA" formatea como YYYY-MM-DD.
const madridDayFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: AGENDA_TIMEZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

export const isDayString = (value) => typeof value === "string" && DAY_RE.test(value);

/** Día natural de Madrid (YYYY-MM-DD) de un instante en epoch segundos. */
export const madridDayOfSeconds = (seconds) => madridDayFormatter.format(new Date(seconds * 1000));

/** Día natural de Madrid de "ahora". Solo para el adaptador temporal: con el contrato nuevo, "hoy"
 * es siempre `meta.today` (lo calcula el backend), nunca el reloj del navegador. */
export const madridToday = (nowMs = Date.now()) => madridDayFormatter.format(new Date(nowMs));

/** Suma días a un YYYY-MM-DD, en aritmética de calendario (sin horas, sin zonas). */
export const addDays = (day, amount) => {
  const [y, m, d] = day.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, d + amount));
  return date.toISOString().slice(0, 10);
};

/** Los días de `from` a `to`, ambos incluidos. */
export const daysBetween = (from, to) => {
  const days = [];
  for (let day = from; day <= to; day = addDays(day, 1)) days.push(day);
  return days;
};

/** Fecha de calendario de un YYYY-MM-DD como Date a mediodía UTC, para formatearla con
 * toLocale*String sin que ninguna zona horaria la mueva de día. */
export const dayToDate = (day) => {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12));
};

/** `finish` vacío = null, undefined o 0 (contrato §19). */
export const hasFinish = (finish) => !!finish;

/** Fin efectivo de un directo en epoch segundos (start + 2 h si no tiene `finish`). */
export const effectiveFinish = (start, finish) => (hasFinish(finish) ? finish : start + AGENDA_DEFAULT_LIVE_SECONDS);

/**
 * Estado de la tarjeta (regla de la pantalla 16): "live" si start ≤ ahora < fin, "scheduled" si
 * ahora < start, "finished" si ya terminó (no debería llegar: upcoming=true lo excluye; un
 * partido que termina con la página abierta se queda hasta la siguiente carga).
 */
export const agendaStatus = (start, finish, nowMs = Date.now()) => {
  if (!start) return null;
  const now = nowMs / 1000;
  if (now < start) return "scheduled";
  if (now < effectiveFinish(start, finish)) return "live";
  return "finished";
};

/**
 * Día abierto válido (`?dia=`) según la respuesta D: tiene que estar dentro de
 * meta.today…meta.until y tener partidos en meta.days (con los filtros vigentes). Si no, se ignora
 * y se abre la lista (contrato §20). Sin `meta` todavía, no se puede validar: null.
 * @param {string} day
 * @param {{today?:string, until?:string, days?:Array<{date:string,count:number}>}} [meta]
 * @returns {string|null}
 */
export const resolveAgendaDay = (day, meta) => {
  if (!isDayString(day) || !meta?.today || !meta?.until) return null;
  if (day < meta.today || day > meta.until) return null;
  const match = (meta.days || []).find((d) => d.date === day);
  return match && match.count > 0 ? day : null;
};
